import { useEffect, useRef } from 'react'

type FullscreenElementWithWebkit = HTMLDivElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}

type FullscreenDocumentWithWebkit = Document & {
  webkitExitFullscreen?: () => Promise<void> | void
  webkitFullscreenElement?: Element | null
}

type UseModalNativeFullscreenParams = {
  active: boolean
  enabled: boolean
  onExit: () => void
}

export default function useModalNativeFullscreen({
  active,
  enabled,
  onExit,
}: UseModalNativeFullscreenParams) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const onExitRef = useRef(onExit)
  const hasEnteredFullscreenRef = useRef(false)

  useEffect(() => {
    onExitRef.current = onExit
  }, [onExit])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const fullscreenDocument = document as FullscreenDocumentWithWebkit
    const handleFullscreenChange = () => {
      const container = containerRef.current
      const fullscreenElement = document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? null
      const isCurrentContainerFullscreen = Boolean(container && fullscreenElement === container)

      if (isCurrentContainerFullscreen) {
        hasEnteredFullscreenRef.current = true
        return
      }

      if (hasEnteredFullscreenRef.current && active) {
        hasEnteredFullscreenRef.current = false
        onExitRef.current()
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener)
    }
  }, [active])

  useEffect(() => {
    if (!active || !enabled || typeof document === 'undefined') {
      return
    }

    const container = containerRef.current as FullscreenElementWithWebkit | null
    const fullscreenDocument = document as FullscreenDocumentWithWebkit

    if (!container) {
      return
    }

    let ignore = false

    const requestFullscreen = async () => {
      try {
        if (document.fullscreenElement === container || fullscreenDocument.webkitFullscreenElement === container) {
          hasEnteredFullscreenRef.current = true
          return
        }

        if (typeof container.requestFullscreen === 'function') {
          await container.requestFullscreen()
          if (!ignore) {
            hasEnteredFullscreenRef.current = true
          }
          return
        }

        if (typeof container.webkitRequestFullscreen === 'function') {
          await container.webkitRequestFullscreen()
          if (!ignore) {
            hasEnteredFullscreenRef.current = true
          }
        }
      } catch {
        hasEnteredFullscreenRef.current = false
      }
    }

    void requestFullscreen()

    return () => {
      ignore = true

      const fullscreenElement = document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? null
      if (fullscreenElement !== container) {
        hasEnteredFullscreenRef.current = false
        return
      }

      if (typeof document.exitFullscreen === 'function') {
        void document.exitFullscreen().catch(() => undefined)
      } else if (typeof fullscreenDocument.webkitExitFullscreen === 'function') {
        void Promise.resolve(fullscreenDocument.webkitExitFullscreen()).catch(() => undefined)
      }

      hasEnteredFullscreenRef.current = false
    }
  }, [active, enabled])

  return containerRef
}
